/**
 * AIStudioIndexDetailPage - one data source, on the shared detail surface.
 *
 * WHAT THIS PAGE IS NOW:
 *
 *     DetailFrame        breadcrumbs, header badges, the overflow menu
 *     DetailTabBar       Details / Files, plus the pane rail - full width
 *     DetailPageSurface  the canvas, with three panes over its edges
 *
 *       context    what the source IS - status, indexer, counts, keywords
 *       assistant  the chat that used to be the "Test" TAB
 *       activity   the audit log that used to be a hand-rolled drawer
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY EVERY LIVE THING STAYS ON THIS COMPONENT
 * ─────────────────────────────────────────────────────────────────────────
 *
 * A pane's BODY is re-created whenever the layout changes, and a pane dragged
 * from the right dock to the left is a different position in the tree, so
 * React unmounts and remounts it. That is fine for a body that only renders
 * props. It is NOT fine for anything with a lifecycle. So this page keeps
 * all three of those on itself, where nothing but leaving the route can
 * unmount them:
 *
 *   1. THE REINDEX SOCKET. Deleting a file can queue a reindex, and the
 *      completion arrives on the `ai_processing` hub. That subscription used
 *      to be opened INSIDE the delete modal's `onConfirm` - no unmount
 *      cleanup at all, so navigating away mid-reindex left a live `*`
 *      listener and a joined group behind for five minutes. It is now one
 *      effect on this component keyed by `isReindexing`: the modal only sets
 *      a flag, the effect joins and leaves, and its cleanup runs on unmount.
 *      Pane moves cannot touch it, because the effect does not live in a
 *      pane.
 *
 *   2. THE SSE UPLOAD. `addDataToDataSourceSSE` streams its progress into
 *      redux (`state.aistudio`), and the await that drives it is
 *      `handleAddData` on this component. The Files TAB renders that progress
 *      from props; unmounting the tab (or any pane) cannot cancel an upload
 *      that no view owns.
 *
 *   3. THE TEST TRANSCRIPT. `chatHistory` / `chatInput` / `isChatLoading`
 *      stay here and are handed to `DataSourceAssistantPane` as props, so
 *      docking the chat left, closing it and reopening it, or switching tabs
 *      underneath it all keep the conversation. As a tab it was thrown away
 *      on every tab switch.
 *
 * The rule for this page: a pane body renders, it does not own.
 */

import React, { useEffect, useRef, useState, Fragment, useCallback } from 'react';
import { getIntlLocale } from '../../../utils/functions';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../../store';
import Button from '../../shared/TailwindComponents/Button';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { useTranslation } from 'react-i18next';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import {
  PiClockLight,
  PiDatabaseLight,
  PiFileLight,
  PiDiamondsFourLight,
  PiTrashLight,
  PiFileTextLight,
  PiDotsThreeVerticalLight,
  PiPaperclipLight,
  PiTestTubeLight,
  PiShareNetworkLight,
  PiInfoLight,
  PiClockCounterClockwiseLight,
} from 'react-icons/pi';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import Badge from '../../shared/TailwindComponents/Badge';
import {
  addDataToDataSourceSSE,
  chatWithDataSource,
  deleteDataSource,
  deleteFileFromDataSource,
  downloadDataSourceFile,
  getDataSourceInfo,
  listFilesInDataSource,
  revokeSharedAccess,
  updateDataSource,
  updateDataSourceSharedAccess,
} from '../../../store/actions/AIStudioAction';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import { getMultiHubSocketService } from '../../../utils/MultiHubSocketService';
import logger from '../../../utils/logger';
import { useSelector } from 'react-redux';
import DataSourceDetailsPanel, { ShareUser } from './DataSourceDetailsPanel';
import DataSourceFiles from './DataSourceFilesPanel';
import { Menu, Transition } from '@headlessui/react';
import DetailFrame from '../../shared/Frames/DetailFrame';
import DetailTabBar, { TabItem } from '../../shared/surface/DetailTabBar';
import DetailPageSurface from '../../shared/surface/DetailPageSurface';
import PaneRail from '../../shared/surface/PaneRail';
import PaneBody from '../../shared/surface/PaneBody';
import AuditLogPanel from '../../AuditLog/AuditLogPanel';
import { DATA_SOURCE_SURFACE, useDataSourceWorkspace } from './surface';
import { buildTabPath, legacyTabRedirect, resolveTabSlug, tabFromUrl } from '../../shared/surface/tabPath';
import { DataSourceSurfaceProvider } from './surface/DataSourceSurfaceContext';
import DataSourceContextPane from './surface/DataSourceContextPane';
import DataSourceAssistantPane, {
  DataSourceChatMessage,
} from './surface/DataSourceAssistantPane';
import ProjectBadges from '../../Projects/ProjectBadges';
import PendingAccessRequests from '../../Projects/PendingAccessRequests';
import AccessPane from '../../shared/access/AccessPane';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, dataSourceDetailTourSteps, dataSourceDetailsPanelTourSteps, dataSourceFilesPanelTourSteps, dataSourceTestTourSteps } from '../../shared/FeatureTour';

/** The hub and group the reindex completion arrives on. */
const REINDEX_HUB = 'ai_processing';
/** Nothing arrived: stop claiming a reindex is running. */
const REINDEX_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * A data source can answer questions once its documents are in the index.
 *
 * The Test pane used to be gated on `isBetaEnv` - which covers eu, app,
 * us.app, beta and demo, i.e. every deployed environment - and read "Coming
 * soon" on all of them, while `POST /datasources/<uuid>/chat` was live and the
 * manual and the feature tour both described the chat as working. Now that
 * setup writes real vectors, the honest gate is the one that was always the
 * real one: whether THIS data source has finished processing.
 */
const READY_STATUSES = new Set(['active', 'ready']);
/** Statuses that mean the index is still being built - resume the live view. */
const INDEXING_STATUSES = new Set(['processing', 'indexing', 'reindexing', 'queued', 'pending_indexing']);

const AIStudioIndexDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { dataSourceUuid, tab: tabParam } = useParams<{ dataSourceUuid: string; tab?: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const [indexInfo, setIndexInfo] = useState<any>(null);
  const [filesInIndex, setFilesInIndex] = useState<{ id?: string; name: string; url?: string }[]>([]);
  const [filesToAdd, setFilesToAdd] = useState<File[]>([]);
  const [isDeleting, setIdDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingFiles, setDeletingFiles] = useState<string[]>([]);
  const [isFilesLoading, setIsFilesLoading] = useState(false);
  /* The Test conversation. On the PAGE, not in the pane - see the header. */
  const [chatHistory, setChatHistory] = useState<DataSourceChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const { complete, connected, steps, isConnecting, ok, summary, startedAt } = useSelector((state: RootState) => state.aistudio)
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [isFetchingFiles, setIsFetchingFiles] = useState(false)
  const [shared, setShared] = useState<ShareUser[]>([]);
  const [isReindexing, setIsReindexing] = useState(false);
  /* Ready = processed and not mid-reprocess. See READY_STATUSES. */
  const isDataSourceReady =
    READY_STATUSES.has(String(indexInfo?.status || '')) && !isReindexing;

  /* ── the surface ────────────────────────────────────────────────── */

  /* The active tab is a PATH segment (`/data-sources/:dataSourceUuid/:tab?`),
     so refresh, deep link and browser back/forward all resolve here. The hook
     still checks the value against the declared tabs and ignores one it does
     not know. */
  const dataSourceBasePath = `/data-sources/${dataSourceUuid}`;
  const {
    panels,
    api: surfaceApi,
    activeTab,
    setActiveTab,
    goToTab,
  } = useDataSourceWorkspace({
    entityId: dataSourceUuid,
    initialTab: resolveTabSlug(DATA_SOURCE_SURFACE.tabs, tabFromUrl(tabParam, location.search)),
    onTabChange: (tab) => history.push(buildTabPath(dataSourceBasePath, tab)),
  });

  /* Old `?tab=<x>` deep links/bookmarks → the new path form, once, preserving
     any other query params. Only when there is no path tab, so no loop. */
  useEffect(() => {
    const target = legacyTabRedirect(dataSourceBasePath, tabParam, location.search);
    if (target) history.replace(target);
  }, [tabParam, location.search, dataSourceBasePath, history]);

  /* Keep the surface's tab in step with the URL so back/forward steps tabs. */
  useEffect(() => {
    setActiveTab(resolveTabSlug(DATA_SOURCE_SURFACE.tabs, tabFromUrl(tabParam, location.search)));
  }, [tabParam, location.search, setActiveTab]);

  const assistantOpen = panels.layout.slots.assistant?.visible ?? false;

  const { isRunning: isDetailTourRunning, handleJoyrideCallback: handleDetailTourCallback } = useFeatureTour({
    tourId: 'data-source-detail',
    steps: dataSourceDetailTourSteps,
    readyToShow: !isLoading && !!indexInfo,
    autoStartDelay: 600,
  });

  const { isRunning: isDetailsPanelTourRunning, handleJoyrideCallback: handleDetailsPanelTourCallback } = useFeatureTour({
    tourId: 'data-source-details-panel',
    steps: dataSourceDetailsPanelTourSteps,
    // Every anchor this tour points at - description, the KPI strip, keywords
    // and formats - is on the Details TAB now. It used to also need the
    // Context pane open, because half of them lived there; a user who had
    // closed that pane got no tour at all.
    readyToShow: !isLoading && !!indexInfo && activeTab === 'Details',
    autoStartDelay: 1200,
  });

  const { isRunning: isFilesPanelTourRunning, handleJoyrideCallback: handleFilesPanelTourCallback } = useFeatureTour({
    tourId: 'data-source-files-panel',
    steps: dataSourceFilesPanelTourSteps,
    readyToShow: !isLoading && activeTab === 'Files' && !isFetchingFiles,
    autoStartDelay: 800,
  });

  const { isRunning: isTestTourRunning, handleJoyrideCallback: handleTestTourCallback } = useFeatureTour({
    tourId: 'data-source-test',
    steps: dataSourceTestTourSteps,
    // The chat is a pane now, so the tour waits for the pane rather than a tab.
    readyToShow: !isLoading && assistantOpen,
    autoStartDelay: 800,
  });

  const tabs: TabItem[] = [
    { label: t('Details'), value: 'Details', icon: <PiFileTextLight className="w-4 h-4" /> },
    { label: t('Files'), value: 'Files', icon: <PiPaperclipLight className="w-4 h-4" /> },
  ];

  const fetchIndexDetails = useCallback(async () => {
    const info = await dispatch(getDataSourceInfo(dataSourceUuid) as any);
    if (info?.status === 'success') {
      setIndexInfo(info.data);
    } else if (info?.status === 'error') {
      dispatch(showFeedbackModal({ showModal: true, message: info.message, modalType: 'error', duration: 3000 }));
    }
  }, [dataSourceUuid, dispatch]);


  const fetchIndexFiles = useCallback(async () => {
    setIsFetchingFiles(true)
    const data = await dispatch(listFilesInDataSource(dataSourceUuid) as any);
    if (data?.status === 'success') {
      setFilesInIndex(data.data.files);
    } else if (data?.status === 'error') {
      dispatch(showFeedbackModal({ showModal: true, message: data.message, modalType: 'error', duration: 3000 }));
    }
    setIsFetchingFiles(false)
  }, [dataSourceUuid, dispatch]);

  /**
   * Re-read everything, through a ref.
   *
   * The reindex effect must not re-subscribe every time one of these
   * callbacks is rebuilt - leaving and rejoining the group mid-reindex is how
   * the completion event gets missed.
   */
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  refreshRef.current = async () => {
    await fetchIndexFiles();
    await fetchIndexDetails();
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await fetchIndexDetails()
      setIsLoading(false);
    };
    fetchData();
  }, [dataSourceUuid, fetchIndexDetails]);

  useEffect(() => {
    if (activeTab === 'Files') {
      fetchIndexFiles()
    }
  }, [activeTab, fetchIndexFiles])

  // A just-created data source navigates here with `dsIndexing` in nav state;
  // a cold click-in on a source still being indexed resumes too. Either way,
  // turn on the live indexing view (the initial index runs through the same
  // /reindex pipeline, so this reuses the existing reindex subscription + UI).
  // Fires once per mount.
  const indexingSeededRef = useRef(false);
  useEffect(() => {
    if (indexingSeededRef.current) return;
    const navIndexing = !!(location.state as any)?.dsIndexing;
    const status = String(indexInfo?.status || '').toLowerCase();
    const statusIndexing = INDEXING_STATUSES.has(status);
    if (navIndexing || statusIndexing) {
      indexingSeededRef.current = true;
      setIsReindexing(true);
      if (navIndexing) history.replace({ pathname: location.pathname, search: location.search });
    }
  }, [indexInfo?.status, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback poll: the reindex subscription is the fast path, but if PubSub
  // never connects we still resolve out of the indexing view by polling status.
  useEffect(() => {
    if (!isReindexing || !dataSourceUuid) return;
    const id = setInterval(async () => {
      try {
        const refreshed: any = await dispatch<any>(getDataSourceInfo(dataSourceUuid));
        const st = String(refreshed?.data?.status || '').toLowerCase();
        if (refreshed?.status === 'success' && (READY_STATUSES.has(st) || st === 'failed')) {
          setIndexInfo(refreshed.data);
          await refreshRef.current?.();
          setIsReindexing(false);
        }
      } catch { /* transient - keep polling */ }
    }, 5000);
    return () => clearInterval(id);
  }, [isReindexing, dataSourceUuid, dispatch]);

  /**
   * THE LIVE REINDEX SUBSCRIPTION - one effect, on the page component.
   *
   * Deliberately keyed on `(isReindexing, dataSourceUuid)` and nothing else,
   * so no amount of pane dragging, tab switching or re-rendering can tear it
   * down and rebuild it. Its cleanup leaves the group and removes the `*`
   * listener on unmount, which the old in-modal version never did.
   *
   * The completion handler sets `isReindexing` to false; that flips this
   * effect off, and the cleanup does the leaving. One exit path.
   */
  useEffect(() => {
    if (!isReindexing || !dataSourceUuid) return;

    const group = `datasource_${dataSourceUuid}`;
    const socketService = getMultiHubSocketService();
    let cancelled = false;
    let handler: ((eventData: any) => void) | null = null;

    const detach = () => {
      if (!handler) return;
      socketService.off(REINDEX_HUB, '*', handler);
      socketService.emit(REINDEX_HUB, 'leave', { group });
      handler = null;
    };

    (async () => {
      try {
        const didConnect = await socketService.connectToHub(REINDEX_HUB);
        if (cancelled) return;
        if (!didConnect) {
          setIsReindexing(false);
          return;
        }
        socketService.emit(REINDEX_HUB, 'join', { group });

        handler = async (eventData: any) => {
          const subType = eventData?.data?.sub_type || '';
          const eventDsUuid = eventData?.data?.datasource_uuid || '';
          if (eventDsUuid !== dataSourceUuid) return;
          if (subType !== 'reindex_completed' && subType !== 'reindex_failed') return;

          await refreshRef.current?.();
          // Turning the flag off re-runs this effect's cleanup, which leaves
          // the group - the listener does not unsubscribe itself.
          setIsReindexing(false);
        };
        socketService.on(REINDEX_HUB, '*', handler);
      } catch (err) {
        logger.warn('[AIStudioIndexDetailPage] PubSub connection failed, falling back', { err });
        if (!cancelled) setIsReindexing(false);
      }
    })();

    // Nothing arrived - stop showing a reindex that may already be over.
    const timer = setTimeout(() => setIsReindexing(false), REINDEX_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      detach();
    };
  }, [isReindexing, dataSourceUuid]);

  // Sync shared_with from indexInfo
  useEffect(() => {
    setShared(indexInfo?.shared_with || []);
  }, [indexInfo?.shared_with]);

  const handleManageAccess = () => {
    dispatch(
      showSidePanel({
        type: 'manageResourceCollaborators',
        showSidePanel: true,
        size: 'md',
        message: t('Manage access'),
        resourceUuid: dataSourceUuid,
        resourceType: 'datasource',
        // Normalize the data-source collaborator shape to what the side panel
        // expects: it reads `profile_picture` and `collaborator_uuid`, while
        // the data-source endpoint emits `avatarUrl` and `id`/`user_uuid`.
        collaborators: (shared || []).map((u: any) => ({
          ...u,
          collaborator_uuid: u.collaborator_uuid || u.user_uuid || u.id,
          user_id: u.user_id || u.id,
          profile_picture: u.profile_picture || u.avatarUrl || u.avatar_url || null,
          access: u.access || 'view',
          source: u.source || 'direct',
        })),
        currentUserUuid: userInfo?.UserUID,
        canAdmin: true,
        addCollaborator: async (uuid: string, userUuid: string, access: string) => {
          await dispatch(updateDataSourceSharedAccess(uuid, [...(shared as any[]), { id: userUuid, email: '', name: '', initials: '', avatarUrl: '', role: '', access: access as any }]) as any);
        },
        removeCollaborator: async (uuid: string, collabUuid: string) => {
          await dispatch(revokeSharedAccess(uuid, collabUuid) as any);
        },
        updateAccess: async (uuid: string, collabUuid: string, access: string) => {
          const updated = (shared as any[]).map((s: any) => s.id === collabUuid ? { ...s, access } : s);
          await dispatch(updateDataSourceSharedAccess(uuid, updated) as any);
        },
        onUpdate: async () => {
          await fetchIndexDetails();
        },
      })
    );
  };





  const handleAddData = async (files: File[]) => {
    if (!dataSourceUuid || files.length === 0) return;
    setIsFilesLoading(true);
    try {
      const resp = await dispatch(addDataToDataSourceSSE(dataSourceUuid, files) as any);

      if (resp?.status === "success") {
        setFilesToAdd([]);
        await fetchIndexFiles();
        await fetchIndexDetails();
      }
    } catch (err) {
      logger.error('[AIStudioIndexDetailPage] AddData SSE failed', { err });
    } finally {
      setIsFilesLoading(false);
    }
  };

  const handleDeleteIndex = async () => {
    dispatch(showModal({
      showModal: true,
      type: "confirmation",
      isDest: true,
      icon:  <PiTrashLight className='w-6 h-6 text-danger-600' />,
      message: t("Delete data source"),
      subMsg: t("This data source and all associated files will be permanently deleted. This action cannot be undone."),
      onConfirm: async () => {
        setIdDeleting(true)
        const resp = await dispatch(deleteDataSource(dataSourceUuid) as any);
        setIdDeleting(false)
        if (resp?.status === 'success') {
          history.push('/data-sources');
        }
        dispatch(hideModal());
      },
      onCancel: () => {},
      duration: 3000,
      inputData: t("Yes, delete")
    })
    )

  };

  /**
   * Ask the data source a question.
   *
   * Unchanged from the Test tab: one POST to `/datasources/:uuid/chat` with
   * the transcript so far, the reply appended on the way back. Only WHERE the
   * transcript is rendered has moved.
   */
  const handleChatSend = useCallback(async () => {
    if (!chatInput.trim() || isChatLoading) return;
    const prompt = chatInput;
    setChatHistory((prev) => [...prev, { role: 'user', content: prompt }]);
    setChatInput('');
    setIsChatLoading(true);

    // Convert history format for API (dataSource -> assistant)
    const apiHistory = chatHistory.map((msg) => ({
      role: msg.role === 'dataSource' ? 'assistant' : msg.role,
      content: msg.content,
    }));

    const response = await dispatch(chatWithDataSource(dataSourceUuid, prompt, apiHistory) as any);
    setIsChatLoading(false);

    if (response) {
      setChatHistory((prev) => [...prev, { role: 'dataSource', content: response as string }]);
    } else {
      dispatch(showFeedbackModal({ showModal: true, message: t('Failed to get a response from the data source.'), modalType: 'error', duration: 3000 }));
    }
  }, [chatInput, chatHistory, isChatLoading, dataSourceUuid, dispatch, t]);

  const handleDeleteFile = (fileId: string, fileName: string) => {
    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete file'),
      subMsg: t('This file will be permanently removed from the data source. This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
      isDest: true,
      onConfirm: async () => {
        setDeletingFiles((prev) => [...prev, fileId]);
        const result = await dispatch(deleteFileFromDataSource(dataSourceUuid, fileId) as any);
        dispatch(hideModal());

        await fetchIndexFiles();
        await fetchIndexDetails();
        setDeletingFiles((prev) => prev.filter((f) => f !== fileId));

        /* The backend queued a reindex. This only raises the FLAG - the
           subscription itself is a page-level effect, so it cannot be left
           dangling by a modal that closes or a pane that moves. */
        if (result?.reindexing) setIsReindexing(true);
      },
      onCancel: () => { },
      showModal: true,
    }));
  };

  const formatDate = (dateString: string | null) =>
    dateString ? new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(dateString)) : t('N/A');

  const handleOpenFile = async (fileId: string | undefined, fileName: string) => {
    if (!fileId) return;

    const ext = (fileName.split('.').pop() || '').toLowerCase();
    const contentTypeMap: Record<string, string> = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      doc: 'application/msword',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      txt: 'text/plain',
      csv: 'text/csv',
    };
    const contentType = contentTypeMap[ext] || 'application/octet-stream';

    // Show reader with loading state
    dispatch(showSidePanel({
      type: 'reader',
      showSidePanel: true,
      width: '65%',
      loading: true,
      message: fileName,
      subMsg: t('Document preview'),
      matterId: '',
      sourceType: 'document',
      citation: {
        title: fileName,
        name: fileName,
        contentType,
      },
    }));

    try {
      const result = await dispatch(downloadDataSourceFile(dataSourceUuid, fileId) as any);
      if (result?.url) {
        // Fetch as blob from the object URL
        const resp = await fetch(result.url);
        const blob = await resp.blob();

        dispatch(showSidePanel({
          type: 'reader',
          showSidePanel: true,
          width: '65%',
          loading: false,
          message: fileName,
          subMsg: t('Document preview'),
          matterId: '',
          sourceType: 'document',
          userAccessLevel: 'view',
          citation: {
            title: fileName,
            name: fileName,
            blob,
            blobObject: blob,
            contentType: result.contentType || contentType,
          },
        }));
      }
    } catch (err) {
      logger.error('[AIStudioIndexDetailPage] Failed to open file', { err });
    }
  };


  const handleDownloadFile = async (fileId: string | undefined, fileName: string) => {
    if (!fileId) return;
    const result = await dispatch(downloadDataSourceFile(dataSourceUuid, fileId) as any);
    if (result?.url) {
      const a = document.createElement('a');
      a.href = result.url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(result.url);
    }
  };


  const handleCopyLink = async (file: { url?: string; name: string }) => {
    if (!file.url) return;
    try {
      await navigator.clipboard.writeText(file.url);
    } catch {
      window.prompt("Copy URL", file.url);
    }
  };




  const handleSaveDataSource = useCallback(async (updates: { name?: string; description?: string; type?: string; keywords?: string[] }) => {
    const result = await dispatch<any>(updateDataSource(dataSourceUuid, updates));
    if (result?.status === 'success') {
      // Refresh data
      const refreshed = await dispatch<any>(getDataSourceInfo(dataSourceUuid));
      if (refreshed?.status === 'success') {
        setIndexInfo(refreshed.data);
      }
      return true;
    }
    return false;
  }, [dataSourceUuid, dispatch]);

const dataSourceBreadcrumbs = [
    { name: t('Data sources'), href: '/data-sources', current: false },
    { name: indexInfo?.DataSourceName || t('Loading...'), href: `/data-sources/${dataSourceUuid}`, current: true },
  ];

  const dataSourceDropdownActions = [
    /* NO "Manage Access" HERE - the rail's Access pill (badged with the
       count) raises the pane that lists who this source is shared with, and
       the pane's own button opens the editor. */
    {
      label: t('Delete data source'),
      onClick: handleDeleteIndex,
      icon: <PiTrashLight className="h-4 w-4 text-danger-900" />,
      variant: 'destructive',
      disabled: isDeleting,
    },
  ];

  const additionalHeaderElements = (
    <div data-tour-id="ds-detail-header" className='flex flex-row gap-3 items-center'>
      <ProjectBadges resourceType="datasource" resourceUuid={dataSourceUuid} />
      <PendingAccessRequests resourceType="datasource" resourceUuid={dataSourceUuid} />
      {/* The faces RAISE THE ACCESS PANE. This was a hand-rolled run of
          `CollaboratorBadge`s with its own "+N" and no way in at all. */}
      <CollaboratorStack
        collaborators={shared || []}
        onClick={() => surfaceApi.openPane('access')}
        testId="datasource-collaborator-stack"
      />
      {/* Actions dropdown */}
      <Menu as="div" className="relative inline-block text-start z-50">
        <Menu.Button as={Button} variant="outline" size="small" className="p-1">
          <PiDotsThreeVerticalLight className="h-4 w-4" />
        </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute end-0 mt-2 w-56 origin-top-right divide-y divide-hairline-soft rounded-md glass shadow-lg focus:outline-none">
          <div className="px-1 py-1">
            {dataSourceDropdownActions.filter(a => a.variant !== 'destructive').map((action, index) => (
              <Menu.Item key={index}>
                {({ active }) => (
                  <button
                    className={`${active ? 'bg-surface-soft text-fg-strong' : 'text-fg-body'} group flex w-full items-center rounded-md px-2 py-2 text-sm gap-2`}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.icon}
                    {t(action.label)}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
          <div className="px-1 py-1">
            {dataSourceDropdownActions.filter(a => a.variant === 'destructive').map((action, index) => (
              <Menu.Item key={index}>
                {({ active }) => (
                  <button
                    className={`${active ? 'bg-danger-50 dark:bg-danger-900/30' : ''} text-danger-700 dark:text-danger-400 group flex w-full items-center rounded-md px-2 py-2 text-sm gap-2`}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.icon}
                    {t(action.label)}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
      </Menu>
    </div>
  );

  const formatSize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let i = -1;
    let size = bytes;
    do {
      size = size / 1024;
      i++;
    } while (size >= 1024 && i < units.length - 1);
    return `${size.toFixed(1)} ${units[i]}`;
  };

  const subHeaderRight = indexInfo && (
    <div data-tour-id="ds-detail-badges" className="flex flex-wrap gap-2">
      <Badge
        children={indexInfo.visibility === 'private' ? t('Personal') : indexInfo.visibility === 'organization' ? t('Company') : t('Shared')}
        tooltip={t('Data source type')}
        icon={<PiDiamondsFourLight size={20} />}
        color='gray'
      />
      <Badge
        children={indexInfo.document_count || 0}
        tooltip={t('Number of files')}
        icon={<PiFileLight size={20} />}
        color='gray'
      />
      <Badge
        children={formatDate(indexInfo.last_indexed_at)}
        tooltip={t('Last run')}
        icon={<PiClockLight size={20} />}
        color='gray'
      />
      <Badge
        children={formatSize(indexInfo.total_size_bytes)}
        tooltip={t('Size')}
        icon={<PiDatabaseLight size={20} />}
        color='gray'
      />
    </div>
  );

  const railIcons = {
    context: <PiInfoLight />,
    assistant: <PiTestTubeLight />,
    access: <PiShareNetworkLight />,
    activity: <PiClockCounterClockwiseLight />,
  };

  return (
    <DataSourceSurfaceProvider value={surfaceApi}>
    <DetailFrame
      breadcrumbs={dataSourceBreadcrumbs}
      dropdownActions={[]}
      subscriptionActive={subscriptionActive}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
      subHeaderRight={subHeaderRight}
      className="animate-fade-in-up h-full flex flex-col"
    >
      {isLoading ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 w-full">
            <DetailTabBar tabs={tabs} activeTab="Details" onTabClick={() => {}} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <Skeleton variant="detail" />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {/*
            THE TABS ARE THEIR OWN ROW, FULL WIDTH, ABOVE THE SURFACE - so a
            docked pane floats below them rather than over them, and the row
            does not shrink as panes open.
          */}
          <div data-tour-id="ds-detail-tabs" className="flex-shrink-0 w-full">
            <DetailTabBar
              tabs={tabs}
              activeTab={activeTab}
              onTabClick={goToTab}
              tourId="ds-detail-tabs-left"
              actions={(
                <PaneRail
                  panels={panels}
                  ariaLabel={t('Panels')}
                  labels="auto"
                  /* Straight from the surface config, so a pane cannot be
                     added and then forgotten here. */
                  items={DATA_SOURCE_SURFACE.railItems({
                    t,
                    icons: railIcons,
                    // The share list's own size, so the pill is right whether
                    // or not the pane has ever been opened.
                    badges: { access: shared?.length || 0 },
                  })}
                />
              )}
            />
          </div>

          <DetailPageSurface
            surface={DATA_SOURCE_SURFACE}
            layoutApi={panels}
            panes={[
              {
                id: 'context',
                title: t('Context'),
                icon: <PiInfoLight />,
                body: (
                  <DataSourceContextPane
                    dataSource={indexInfo}
                    isReindexing={isReindexing}
                  />
                ),
              },
              {
                id: 'assistant',
                title: t('Test'),
                icon: <PiTestTubeLight />,
                /* PROPS ONLY. The transcript lives on the page, so dragging
                   this pane to the other dock - which remounts the body —
                   keeps the conversation. */
                body: (
                  <DataSourceAssistantPane
                    messages={chatHistory}
                    input={chatInput}
                    onInputChange={setChatInput}
                    onSend={handleChatSend}
                    isSending={isChatLoading}
                    disabled={!isDataSourceReady}
                    disabledLabel={isReindexing || !indexInfo
                      ? t('Available once processing completes')
                      : t('Add files and let processing finish to ask questions')}
                  />
                ),
              },
              {
                id: 'access',
                title: t('Access'),
                icon: <PiShareNetworkLight />,
                body: (
                  <AccessPane
                    collaborators={shared || []}
                    onManageAccess={handleManageAccess}
                    canManage
                    /* A data source has no creator row in `shared_with`, so
                       there is nothing to pin - everyone in the list is a
                       grant. */
                    pinOwner={false}
                  />
                ),
              },
              {
                id: 'activity',
                title: t('Activity'),
                icon: <PiClockCounterClockwiseLight />,
                /* A thunk: the log fetches on mount, so it is built only
                   while the pane is actually on screen. No `hideHeader` —
                   it reads `useInPane()`. */
                body: () => (
                  <PaneBody>
                    <AuditLogPanel
                      entityType="DataSource"
                      entityId={dataSourceUuid}
                      entityName={indexInfo?.DataSourceName || indexInfo?.name || ''}
                      onClose={() => panels.setVisible('activity', false)}
                    />
                  </PaneBody>
                ),
              },
            ]}
          >
            {activeTab === 'Details' && (
              <div className="flex-1 min-h-0">
                <DataSourceDetailsPanel
                  loading={false}
                  dataSource={indexInfo}
                  canEdit={true}
                  onSave={handleSaveDataSource}
                  t={t}
                />
              </div>
            )}

            {activeTab === 'Files' && (
              <DataSourceFiles
                t={t}
                filesInIndex={filesInIndex}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                deletingFiles={deletingFiles}
                handleOpenFile={handleOpenFile}
                handleDownloadFile={handleDownloadFile}
                handleCopyLink={handleCopyLink}
                handleDeleteFile={handleDeleteFile}
                filesToAdd={filesToAdd}
                setFilesToAdd={setFilesToAdd}
                handleAddData={handleAddData}
                isFilesLoading={isFilesLoading}
                isConnecting={isConnecting}
                steps={steps}
                connected={connected}
                complete={complete}
                ok={ok}
                summary={summary}
                startedAt={startedAt}
                isFetchingFiles={isFetchingFiles}
                isReindexing={isReindexing}
              />
            )}
          </DetailPageSurface>
        </div>
      )}
      <FeatureTour steps={dataSourceDetailTourSteps} run={isDetailTourRunning} onCallback={handleDetailTourCallback} continuous showProgress showSkipButton disableOverlay />
      <FeatureTour steps={dataSourceDetailsPanelTourSteps} run={isDetailsPanelTourRunning} onCallback={handleDetailsPanelTourCallback} continuous showProgress showSkipButton disableOverlay />
      <FeatureTour steps={dataSourceFilesPanelTourSteps} run={isFilesPanelTourRunning} onCallback={handleFilesPanelTourCallback} continuous showProgress showSkipButton disableOverlay />
      <FeatureTour steps={dataSourceTestTourSteps} run={isTestTourRunning} onCallback={handleTestTourCallback} continuous showProgress showSkipButton disableOverlay />
    </DetailFrame>
    </DataSourceSurfaceProvider>
  );
};

export default AIStudioIndexDetailPage;
